import { useCallback, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import {
  DataGridPremium,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
} from "@mui/x-data-grid-premium";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { DataGridManageColumnsPanel } from "../../components/shared/DataGridManageColumnsPanel";
import ThinCheckbox from "../../components/shared/ThinCheckbox";
import {
  aiPlanDataGridPanelSx,
  getDataGridEmptyStateSx,
  getRegulationsListDataGridSx,
  aiHubListDataGridRowBorderSx,
} from "../../components/shared/regulationsListDataGridSx";
import styles from "../../components/RegulationsTable/RegulationsTable.module.css";
import AiHubNoRowsOverlay from "./AiHubNoRowsOverlay";
import { AiHubFilterLabelCell, AiHubObjectIdCell } from "./aiHubTableCells";
import AiHubTableToolbar from "./AiHubTableToolbar";
import { ENHANCED_TAGGING_RESULT_EXPORT_COPY } from "./exportCopy";
import TaggingResultRowActions from "./TaggingResultRowActions";
import {
  downloadConditionalTaggingResultsExcel,
  type ConditionalTaggingResultRow,
} from "./conditionalTaggingResultData";
import { useHasPrivilege } from "../../hooks/useHasPrivilege";
import { TAGGING_RESULT_PRIVILEGES } from "../../services/permissionsService";
import { useCrossPageRowSelection } from "./useCrossPageRowSelection";

type ConditionalTaggingResultTableProps = Readonly<{
  rows: ConditionalTaggingResultRow[];
  scopeObjectType: string;
  scopeObjectSubType: string;
  objectTypeLabel: string;
  objectSubTypeLabel: string;
  rowCount: number;
  selectionResetKey: string;
  loading?: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  objectIdSearch: string;
  onObjectIdSearchChange: (value: string) => void;
}>;

export default function ConditionalTaggingResultTable({
  rows,
  scopeObjectType,
  scopeObjectSubType,
  objectTypeLabel,
  objectSubTypeLabel,
  rowCount,
  selectionResetKey,
  loading = false,
  paginationModel,
  onPaginationModelChange,
  objectIdSearch,
  onObjectIdSearchChange,
}: ConditionalTaggingResultTableProps) {
  const theme = useTheme();
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const panelScrollTopRef = useRef(0);
  const {
    selectionModel,
    onRowSelectionModelChange,
    isRowSelectable,
    selectedRows,
  } = useCrossPageRowSelection(rows, selectionResetKey);
  const canExportTaggingResult = useHasPrivilege(
    TAGGING_RESULT_PRIVILEGES.read,
  );

  const columnsManagementSlot = useCallback(
    () => (
      <DataGridManageColumnsPanel
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        scrollTopRef={panelScrollTopRef}
      />
    ),
    [columnVisibilityModel],
  );

  const handleExport = useCallback(
    () =>
      downloadConditionalTaggingResultsExcel(selectedRows, {
        objectTypeLabel,
        objectSubTypeLabel,
      }),
    [objectSubTypeLabel, objectTypeLabel, selectedRows],
  );

  const toolbarSlot = useCallback(
    () => (
      <AiHubTableToolbar
        exportTooltip={ENHANCED_TAGGING_RESULT_EXPORT_COPY.selectedTooltip}
        emptyExportTooltip={ENHANCED_TAGGING_RESULT_EXPORT_COPY.emptyTooltip}
        hasExportSelection={selectedRows.length > 0}
        exportFailureMessage={ENHANCED_TAGGING_RESULT_EXPORT_COPY.failureMessage}
        onExport={handleExport}
        showExport={canExportTaggingResult}
        objectIdSearch={objectIdSearch}
        onObjectIdSearchChange={onObjectIdSearchChange}
      />
    ),
    [
      canExportTaggingResult,
      handleExport,
      objectIdSearch,
      onObjectIdSearchChange,
      selectedRows.length,
    ],
  );

  const hasScopeSelection = Boolean(
    scopeObjectType.trim() || scopeObjectSubType.trim(),
  );
  const noRowsOverlaySlot = useCallback(
    () => <AiHubNoRowsOverlay hasScopeSelection={hasScopeSelection} />,
    [hasScopeSelection],
  );

  const columns = useMemo<GridColDef<ConditionalTaggingResultRow>[]>(
    () => [
      {
        field: "objectTypeName",
        headerName: "Object Type",
        width: 140,
        sortable: false,
        filterable: false,
        valueGetter: () => objectTypeLabel.trim() || "-",
        renderCell: () => <AiHubFilterLabelCell label={objectTypeLabel} />,
      },
      {
        field: "objectSubTypeName",
        headerName: "Object Sub-Type",
        width: 160,
        sortable: false,
        filterable: false,
        valueGetter: () => objectSubTypeLabel.trim() || "-",
        renderCell: () => <AiHubFilterLabelCell label={objectSubTypeLabel} />,
      },
      {
        field: "objectId",
        headerName: "Object ID",
        width: 160,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <AiHubObjectIdCell id={row.objectId} link={row.link} />
        ),
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1,
        minWidth: 190,
        sortable: false,
        filterable: false,
      },
      {
        field: "version",
        headerName: "Version",
        width: 90,
        sortable: false,
        filterable: false,
      },
      {
        field: "taggingResults",
        headerName: "Classification Results",
        flex: 1.25,
        minWidth: 230,
        sortable: false,
        filterable: false,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 86,
        sortable: false,
        filterable: false,
        resizable: false,
        disableExport: true,
        align: "center",
        headerAlign: "center",
        cellClassName: "ai-hub-actions-cell",
        renderCell: ({ row }) => (
          <TaggingResultRowActions
            rowId={row.objectId}
            objectType={row.objectType || scopeObjectType}
            objectSubType={row.objectSubType || scopeObjectSubType}
            version={row.version}
            resultType="conditional"
          />
        ),
      },
    ],
    [objectSubTypeLabel, objectTypeLabel, scopeObjectSubType, scopeObjectType],
  );

  const isEmpty = rows.length === 0;

  return (
    <Box
      className={`${styles.dataGridPanel}${
        isEmpty ? ` ${styles.dataGridPanelEmpty}` : ""
      }`}
      sx={{
        ...aiPlanDataGridPanelSx,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 480,
      }}
    >
      <DataGridPremium
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        rowHeight={48}
        checkboxSelection
        keepNonExistentRowsSelected
        disableRowSelectionOnClick
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        isRowSelectable={isRowSelectable}
        disableRowGrouping
        disableAggregation
        disablePivoting
        disableColumnSorting
        disableColumnFilter
        loading={loading}
        pagination
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[5, 10, 20, 50]}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        showToolbar
        showColumnVerticalBorder={false}
        pinnedColumnsSectionSeparator="border"
        slots={{
          toolbar: toolbarSlot,
          noRowsOverlay: noRowsOverlaySlot,
          columnsManagement: columnsManagementSlot,
          baseCheckbox: ThinCheckbox,
        }}
        initialState={{ pinnedColumns: { right: ["actions"] } }}
        sx={
          [
            getRegulationsListDataGridSx(theme.palette.primary.main),
            aiHubListDataGridRowBorderSx,
            ...(isEmpty ? [getDataGridEmptyStateSx()] : []),
            {
              minHeight: 400,
              "& .MuiDataGrid-toolbarQuickFilter": { display: "none" },
              "& .ai-hub-actions-cell": {
                display: "flex !important",
                alignItems: "center !important",
                justifyContent: "center !important",
              },
            },
          ] as SxProps<Theme>
        }
      />
    </Box>
  );
}
